import { useCallback, useEffect, useState } from 'react';
import { Banner, BlockStack, Text } from '@shopify/polaris';
import apiClient from '../utils/api';
import { errorSummary } from '../utils/sync-error-message';

/**
 * A warning ON THE TABLE when the run that produced its rows did not go cleanly.
 *
 * Every table on these screens shows the RESULT of a background run — the SKU map shows what
 * the last catalogue fetch wrote, Retailer Orders what the last order fetch collected. When
 * that run fails half way, or writes fewer rows than the retailer sent, the table is short by
 * exactly that much and says nothing: "4,299 SKUs held" reads identically whether the fetch
 * finished or died at 2,000.
 *
 * The failure WAS recorded — in Sync History, on a different screen, which nobody opens while
 * reading a number that looks plausible. This puts it where the wrong number is.
 *
 * It is deliberately not a substitute for Sync History. It carries one sentence and a time,
 * and points at the full record; the run's own page has the counts and the whole message.
 */

/** Which run each table's rows actually come from. Exported so a caller names a fact, not a key. */
export const RUN_TYPES = {
  RETAILER_CATALOGUE: ['SKU_MAP_REFRESH'],
  // Both sides of the comparison: the retailer's offers, and our own Shopify mirror. A stale
  // or short reading of EITHER makes every difference on that screen suspect, and the screen
  // cannot tell you which side is wrong from the numbers alone.
  SKU_MAP: ['SKU_MAP_REFRESH', 'CATALOGUE_SYNC'],
  RETAILER_ORDERS: ['ORDER_SYNC'],
};

/** Plain English for the run types this banner can name. */
const RUN_LABELS = {
  SKU_MAP_REFRESH: 'fetching SKUs from the retailer',
  CATALOGUE_SYNC: 'reading the Shopify catalogue',
  ORDER_SYNC: 'fetching orders from the retailer',
};

const when = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

export default function RunHealthBanner({ marketplaceId = null, brandId = null, types = [] }) {
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    try {
      setHealth(await apiClient.getRunHealth({ marketplaceId, brandId }));
    } catch {
      // A banner that cannot load is not itself news. Failing quietly beats a red box on
      // every table saying the health check is down — that would be the false alarm this
      // component exists to avoid.
      setHealth(null);
    }
  }, [marketplaceId, brandId]);

  useEffect(() => { load(); }, [load]);

  if (!health) return null;

  // Only the runs THIS table's rows came from. A failed order fetch is not news on the
  // catalogue screen, and a banner that fires on every table for every failure is one
  // people learn to scroll past.
  const problems = types
    .map((t) => ({ type: t, run: health[t] }))
    .filter(({ run }) => run && run.healthy === false);

  if (problems.length === 0) return null;

  // Critical only for an outright failure. A completed run that left a note did finish — the
  // rows it wrote are real — so it is a caveat on the figures, not a broken screen, and
  // toning it the same as a failure would make both easy to ignore.
  const anyFailed = problems.some(({ run }) => run.status === 'FAILED');

  return (
    <Banner tone={anyFailed ? 'critical' : 'warning'}>
      <BlockStack gap="100">
        <Text variant="bodySm" fontWeight="semibold">
          {anyFailed
            ? 'The numbers below are incomplete.'
            : 'The numbers below may be incomplete.'}
        </Text>
        {problems.map(({ type, run }) => {
          const what = RUN_LABELS[type] || 'a background run';
          const at = when(run.at);
          return (
            <Text key={type} variant="bodySm" breakWord>
              {run.status === 'FAILED'
                ? `The last run ${what}${run.retailerName ? ` for ${run.retailerName}` : ''} failed`
                : `The last run ${what}${run.retailerName ? ` for ${run.retailerName}` : ''} finished with a caveat`}
              {at ? ` (${at})` : ''}
              {': '}
              {/* The plain wording here too. This banner sits over the figures a run
                  produced, and a stack trace above a stock count helps nobody — the whole
                  message is one click away in Sync History. */}
              {errorSummary(run.error) || run.note || 'no reason was recorded.'}
              {' '}
              Anything it did not write is missing from this table until it runs again.
            </Text>
          );
        })}
        <Text variant="bodySm" tone="subdued">
          The full record, with what each run got through, is under Background Jobs -&gt; Sync History.
        </Text>
      </BlockStack>
    </Banner>
  );
}
